import React, { useRef, useState } from "react";
import { VideoPlayerHandle } from "../Video/VideoPlayer";
import VideoPlayer from "../Video/VideoPlayer";
import { UserVideoWord, Card } from "@shared_types";
import ReviewContent from "./ReviewContent";
import TrashButton from "../Buttons/TrashButton";
import ReviewButton from "../Buttons/ReviewButton";
import ContinueButton from "../Buttons/ContinueButton";
import { getPlatform } from "@/lib/iap";
import { useDivSize } from "@/hooks/UseDivSize";
import ParrotSpinner from "@/components/ParrotSpinner";

interface ReviewCardProps {
  card: Card | null;
  isFlipped: boolean;
  onTrackListeningTime: (time: number) => void;
  handleFlip: () => void;
  handleReviewGood: (cardId: number) => Promise<void>;
  handleReviewAgain: (cardId: number) => Promise<void>;
  handleDeleteCard: (cardId: number) => Promise<void>;
}

const ReviewCard: React.FC<ReviewCardProps> = ({
  card,
  isFlipped,
  onTrackListeningTime,
  handleFlip,
  handleReviewGood,
  handleReviewAgain,
  handleDeleteCard,
}) => {
  const platform = getPlatform();
  const isHorizontal = card?.aspectRatio === "16:9";
  const [selectedWord, setSelectedWord] = useState({} as UserVideoWord);
  const clickedWord = selectedWord?.displayText || "";
  const clickedNativeWord = selectedWord?.definition?.nativeWord || "";
  const [currentTime, setCurrentTime] = useState<number | null>(null);
  const [isDisplayHelp, setIsDisplayHelp] = useState(true);
  const videoRef = useRef<VideoPlayerHandle>(null);
  const videoEndMs = card?.videoEndMs;

  const pauseHandleTimeUpdate = useRef(false);
  const { ref: reviewPageRef, height: reviewPageHeight } = useDivSize();
  const { ref: reviewCardContentRef, height: reviewCardHeight } = useDivSize();

  let bottomPadding = 0;
  if (isHorizontal) {
    bottomPadding = 0;
  }
  const videoPlayerHeight = `${
    reviewPageHeight - reviewCardHeight - bottomPadding + 2
  }px`;

  const reviewCardStyleHeight = "calc(var(--video-content-height) + 20px)";

  const handleWordClick = async (word: UserVideoWord) => {
    pauseHandleTimeUpdate.current = true;
    setSelectedWord(word);
    setCurrentTime(word.start);
    setIsDisplayHelp(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.seekTo(word.start);
    }
  };

  const handleTimeUpdate = (timeMs: number) => {
    if (!pauseHandleTimeUpdate.current) {
      setCurrentTime(timeMs);
    }
  };

  const handlePlay = () => {
    pauseHandleTimeUpdate.current = false;
  };

  // If card is null, render loading skeleton
  if (!card) {
    return (
      <div
        ref={reviewPageRef}
        className={`relative flex flex-col items-center w-full h-full justify-between max-w-[700px]`}
      >
        {/* Video skeleton with ParrotSpinner */}
        <div
          className="w-full flex items-center justify-center"
          style={{ height: videoPlayerHeight || "300px" }}
        >
          <ParrotSpinner size="xl" />
        </div>

        {/* Content skeleton */}
        <div
          ref={reviewCardContentRef}
          className={`absolute bottom-0 flex flex-col justify-end flex-grow sm:gap-2 w-full max-h-[330px] h-[39%] gap-2 pb-0 bg-[#fafafa] px-4 rounded-t-xl`}
          style={{ minHeight: reviewCardStyleHeight, height: "auto" }}
        >
          <div className="flex flex-col flex-grow w-full pt-3 justify-center items-center relative">
            {/* Text placeholder */}
            <div className="w-full flex flex-col items-center space-y-2 py-4">
              <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/4 animate-pulse"></div>
              <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/2 animate-pulse"></div>
              <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-2/3 animate-pulse"></div>
            </div>

            {/* Trash button placeholder */}
            <span className="absolute right-0 bottom-0">
              <div className="h-8 w-8 rounded-full bg-gray-300 dark:bg-gray-700 animate-pulse"></div>
            </span>
          </div>

          {/* Button placeholder */}
          <div
            className={`flex flex-col gap-2 w-full justify-end items-center z-10 ${
              platform === "web" ? "pb-3" : "pb-6"
            }`}
          >
            <div className="h-10 bg-gray-300 dark:bg-gray-700 rounded w-full animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  let contextText = card.contextText;
  const contextTextLength = contextText.length;

  const contextIsSingleWord = contextTextLength == 1;
  const contextTwoWords = contextTextLength == 2;

  let startTime = 0;
  let endTime = 0;

  if (!contextIsSingleWord) {
    startTime = card.contextText[0].start;
    const endWord = card.contextText[contextTextLength - 1];
    const endBuffer = calcEndBuffer(endWord);
    endTime = endWord.start + endBuffer;
  } else {
    startTime = card.contextText[0].start;
    const endWord = card.contextText[0];
    const endBuffer = calcEndBuffer(endWord);
    endTime = endWord.start + endBuffer;
  }

  if (contextIsSingleWord || contextTwoWords) {
    // do nothing times are set above
  } else if (contextText[contextTextLength - 1] && endTime !== 0) {
    const endWord = contextText[contextTextLength - 1];
    const endBuffer = calcEndBuffer(endWord);
    endTime = endWord.start + endBuffer;
  }

  if (videoEndMs && endTime > videoEndMs) {
    endTime = videoEndMs;
  }

  return (
    <div
      ref={reviewPageRef}
      className={`relative flex flex-col items-center w-full h-full justify-between max-w-[700px]`}
    >
      <VideoPlayer
        ref={videoRef}
        youtubeId={card.youtubeId}
        onTimeUpdate={handleTimeUpdate}
        startTime={startTime}
        endTime={endTime}
        onPlay={handlePlay}
        onTrackListeningTime={onTrackListeningTime}
        isHorizontal={isHorizontal}
        videoPlayerHeight={videoPlayerHeight}
      />
      <div
        ref={reviewCardContentRef}
        className={`absolute bottom-0 flex flex-col justify-end flex-grow sm:gap-2 w-full max-h-[330px] h-[39%] gap-2 pb-0 bg-[#fafafa] px-4 rounded-t-xl`}
        style={{ minHeight: reviewCardStyleHeight, height: "auto" }}
      >
        <div className="flex flex-col flex-grow w-full pt-3 justify-center items-center relative">
          <ReviewContent
            currentTime={currentTime as number}
            contextText={contextText}
            reviewWord={card.reviewWord}
            targetWord={clickedWord}
            nativeWord={clickedNativeWord}
            isFlipped={isFlipped}
            onWordClick={handleWordClick}
            isDisplayHelp={isDisplayHelp}
          />
          <span className="absolute right-0 bottom-0">
            <TrashButton onDelete={() => handleDeleteCard(card.id)} />
          </span>
        </div>
        <div
          className={`flex flex-col gap-2 w-full justify-end items-center z-10 ${
            platform === "web" ? "pb-3" : "pb-6"
          }`}
        >
          {!isFlipped ? (
            <ContinueButton onClick={handleFlip} text="Flip for definition" />
          ) : (
            <div className="flex flex-row w-full items-center justify-center gap-2">
              <ReviewButton
                bgColor={"var(--color-green)"}
                onClick={() => handleReviewGood(card.id)}
                text="Good"
              />
              <ReviewButton
                bgColor={"var(--color-red)"}
                onClick={() => handleReviewAgain(card.id)}
                text="Try Later"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function calcEndBuffer(word: UserVideoWord) {
  if (word.duration === 0) {
    return 1500;
  }
  return word.duration + 200;
}

export default ReviewCard;
